import { PERMISSIONS, type Permission } from "./access/accessModel";
import { ACCOUNT_ROLES, type AccountRole } from "./accountsApi";
import { addProcessTerms, readProcessTerms, type ProcessTerms } from "./content/processTermWords";
import { enumMembers, type ApiSchema } from "./apiSchema";
import { readStringList } from "./content/contentDecoders";
import { jsonBody, requestJson } from "./http/httpClient";
import { readArray, readNullableString, readObject, readOneOf, readString, type JsonObject } from "./http/jsonDecoding";
import { appPaths } from "../shared/routing/appPaths";

/**
 * Invitations (accounts.md GS-7): an invitation carries groups, which the person joins on
 * accepting, with no approval; a project's group carries its project, one project at most.
 * Whoever may manage a group may invite into it, which the server checks when it is sent and
 * again when it is accepted. DMW sends no mail yet, so the inviter passes the acceptance link on.
 */

export interface InvitationRequest {
  readonly email: string;
  /** `administrator` makes them one of the installation's Administrators. */
  readonly accountRole: AccountRole;
  /** The groups it carries. */
  readonly groups: readonly string[];
}

export interface IssuedInvitation {
  readonly invitationId: string;
  readonly email: string;
  readonly expiresAt: string;
  /** Shown once: the server keeps only its digest. */
  readonly acceptanceToken: string;
}

export type InvitationState = ApiSchema<"InvitationState">;

const INVITATION_STATES = enumMembers<InvitationState>({ pending: true, accepted: true, revoked: true, expired: true });

/** An invitation with what it offers, in names a person reads (F133). */
export interface InvitationOffer {
  readonly invitationId: string;
  readonly email: string;
  readonly accountRole: AccountRole;
  readonly state: InvitationState;
  /** ISO 8601. */
  readonly expiresAt: string;
  /** Empty if the inviter's account is gone. */
  readonly invitedBy: string;
  readonly projectName: string | null;
  /** The groups it puts the person into, by name; one made before groups carried them names its role's starter group too. */
  readonly groupNames: readonly string[];
}

/** What the invitation's link holder gets once active, as the server works it out (F133). */
export interface InvitationPreview extends InvitationOffer {
  /** In the invited project; `null` without one. */
  readonly holding: {
    /** What they may do there, Curate aside (accounts.md GS-2), in screen order. */
    readonly permissions: readonly Permission[];
    readonly curatedKinds: readonly string[];
    /** The groups that give something there, by name. */
    readonly groups: readonly string[];
  } | null;
  /** Every group they join, the default ones included. */
  readonly joins: readonly string[];
  /** The ontology's words for the kinds they will curate: the holder isn't signed in yet. */
  readonly kindWords: ProcessTerms;
}

export function invite(request: InvitationRequest): Promise<IssuedInvitation> {
  const body = {
    email: request.email,
    account_role: request.accountRole,
    groups: [...request.groups],
  } satisfies ApiSchema<"InvitationBody">;
  return requestJson({
    method: "POST",
    path: "/accounts/invitations",
    body: jsonBody(body),
    decodeResponse: decodeIssuedInvitation,
  });
}

/** The installation's invitations (administrators), newest first. */
export function listInvitations(signal?: AbortSignal): Promise<readonly InvitationOffer[]> {
  return requestJson({
    method: "GET",
    path: "/accounts/invitations",
    signal,
    decodeResponse: (body) => readArray(body, "invitations", (item) => decodeInvitationOffer(readObject(item, "invitation"))),
  });
}

export function revokeInvitation(invitationId: string): Promise<void> {
  return requestJson({
    method: "POST",
    path: `/accounts/invitations/${encodeURIComponent(invitationId)}/revoke`,
    decodeResponse: () => undefined,
  });
}

/** What the invitation with this token offers, before accepting it. */
/** What an invitation's link says to whoever holds it: its state, and while it is pending what
 * they get; a finished invitation shows nothing it offered. */
export interface InvitationLink {
  readonly state: InvitationState;
  readonly preview: InvitationPreview | null;
}

export function previewInvitation(acceptanceToken: string, signal?: AbortSignal): Promise<InvitationLink> {
  const body = { acceptance_token: acceptanceToken } satisfies ApiSchema<"InvitationPreviewBody">;
  return requestJson({
    method: "POST",
    path: "/accounts/invitations/preview",
    body: jsonBody(body),
    signal,
    decodeResponse: (response) => decodeInvitationLink(readObject(response, "invitation link")),
  }).then((link) => {
    // Its kinds by the ontology's names, as everywhere else, before the app reads all of them.
    if (link.preview !== null) addProcessTerms(link.preview.kindWords);
    return link;
  });
}

export function decodeInvitationLink(link: JsonObject): InvitationLink {
  const preview = link["preview"];
  return {
    state: readOneOf(link, "state", INVITATION_STATES),
    preview: preview === null ? null : decodeInvitationPreview(readObject(preview, "invitation preview")),
  };
}

function decodeInvitationPreview(invitation: JsonObject): InvitationPreview {
  const holding = invitation["holding"];
  return {
    ...decodeInvitationOffer(invitation),
    holding: holding === null ? null : decodeOfferedHolding(readObject(holding, "offered holding")),
    joins: readStringList(invitation, "joins"),
    kindWords: readProcessTerms(invitation["kind_words"]),
  };
}

function decodeOfferedHolding(holding: JsonObject): NonNullable<InvitationPreview["holding"]> {
  const granted = new Set(readArray(holding["grants"], "offered grants", (item) => readOneOf(readObject(item, "offered grant"), "permission", PERMISSIONS)));
  return {
    permissions: PERMISSIONS.filter((permission) => permission !== "curate" && granted.has(permission)),
    curatedKinds: readStringList(holding, "curated_kinds"),
    groups: readStringList(holding, "groups"),
  };
}

export function decodeIssuedInvitation(response: unknown): IssuedInvitation {
  const issued = readObject(response, "issued invitation");
  const invitation = readObject(issued["invitation"], "invitation");
  return {
    invitationId: readString(invitation, "id"),
    email: readString(invitation, "email"),
    expiresAt: readString(invitation, "expires_at"),
    acceptanceToken: readString(issued, "acceptance_token"),
  };
}

export function decodeInvitationOffer(invitation: JsonObject): InvitationOffer {
  return {
    invitationId: readString(invitation, "id"),
    email: readString(invitation, "email"),
    accountRole: readOneOf(invitation, "account_role", ACCOUNT_ROLES),
    state: readOneOf(invitation, "state", INVITATION_STATES),
    expiresAt: readString(invitation, "expires_at"),
    invitedBy: readString(invitation, "invited_by_name"),
    projectName: readNullableString(invitation, "project_name"),
    groupNames: readStringList(invitation, "group_names"),
  };
}

/** The acceptance page's address with the token in the fragment, which browsers never send to a server. */
export function invitationLink(acceptanceToken: string, origin: string): string {
  return `${origin}${appPaths.invitationAcceptance()}#${new URLSearchParams({ token: acceptanceToken }).toString()}`;
}
